/**
 * FountainDetailsPanel - Panel de detalles de fuente/bebedero
 *
 * @version 1.0
 * @updated 2026-02-27
 * @module core/map-tools
 *
 * @description
 * Panel para gestión de fuentes de agua potable y bebederos públicos.
 * Compatible con Entity Registry System.
 */

import { useState, useEffect, useMemo } from 'react';
import { Copy, Check, Trash2, Droplets } from 'lucide-react';
import type { Fountain, FountainType, FountainStatus, WaterQuality, AccessibilityLevel } from '../fountain-types';
import { useRightPanel, useUnsavedChanges } from '../../layouts/panels/RightPanel';
import { ConfirmDialog } from '../../components';
import './fountain-details-panel.css';

/**
 * Tipo de pestañas disponibles
 */
type TabId = 'general' | 'operational' | 'maintenance' | 'accessibility';

/**
 * Props del componente
 */
export interface FountainDetailsPanelProps {
  /**
   * Datos de la fuente a mostrar/editar
   */
  fountain: Fountain;

  /**
   * Callback cuando se guarda la fuente
   */
  onSave: (updatedFountain: Fountain) => void;

  /**
   * Callback cuando se cancela la edición
   */
  onCancel: () => void;

  /**
   * Callback cuando se elimina la fuente
   */
  onDelete: (fountainId: string) => void;

  /**
   * Si true, el panel es de solo lectura
   */
  readOnly?: boolean;
}

/**
 * Opciones de tipo de fuente
 */
const FOUNTAIN_TYPE_OPTIONS: { value: FountainType; label: string }[] = [
  { value: 'drinking', label: 'Bebedero' },
  { value: 'decorative', label: 'Decorativa' },
  { value: 'mixed', label: 'Mixta' },
];

/**
 * Opciones de estado
 */
const FOUNTAIN_STATUS_OPTIONS: { value: FountainStatus; label: string }[] = [
  { value: 'active', label: 'Activa' },
  { value: 'inactive', label: 'Inactiva' },
  { value: 'maintenance', label: 'En mantenimiento' },
  { value: 'damaged', label: 'Dañada' },
];

/**
 * Opciones de calidad de agua
 */
const WATER_QUALITY_OPTIONS: { value: WaterQuality; label: string }[] = [
  { value: '', label: 'No aplica' },
  { value: 'excellent', label: 'Excelente' },
  { value: 'good', label: 'Buena' },
  { value: 'fair', label: 'Regular' },
  { value: 'poor', label: 'Pobre' },
];

/**
 * Opciones de accesibilidad
 */
const ACCESSIBILITY_OPTIONS: { value: AccessibilityLevel; label: string }[] = [
  { value: '', label: 'No especificado' },
  { value: 'full', label: 'Total' },
  { value: 'partial', label: 'Parcial' },
  { value: 'limited', label: 'Limitada' },
];

/**
 * Componente FountainDetailsPanel
 */
export function FountainDetailsPanel({
  fountain,
  onSave,
  onCancel,
  onDelete,
  readOnly = false,
}: FountainDetailsPanelProps): JSX.Element {
  const { registerUnsavedChangesCheck } = useRightPanel();
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [formData, setFormData] = useState<Fountain>(fountain);
  const [originalData, setOriginalData] = useState<Fountain>(fountain);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /**
   * Detecta si es una fuente nueva (no persistida)
   */
  const isNewFountain = useMemo(() => {
    return fountain.id.startsWith('fountain-') || fountain.code?.startsWith('TEMP-');
  }, [fountain.id, fountain.code]);

  // Hook para detectar cambios sin guardar
  const { hasUnsavedChanges, confirmDiscard } = useUnsavedChanges(
    originalData,
    formData,
    {
      confirmMessage: isNewFountain
        ? '¿Descartar fuente sin guardar?\n\nLa fuente desaparecerá del mapa.'
        : '¿Descartar cambios de la fuente?\n\nLos cambios se perderán.',
    }
  );

  /**
   * Registrar función de verificación de cambios sin guardar
   */
  useEffect(() => {
    registerUnsavedChangesCheck(confirmDiscard);
    return () => {
      registerUnsavedChangesCheck(null);
    };
  }, [confirmDiscard, registerUnsavedChangesCheck]);

  /**
   * Resetear form cuando cambia la fuente
   */
  useEffect(() => {
    setFormData(fountain);
    setOriginalData(fountain);
  }, [fountain]);

  /**
   * Actualiza los datos del formulario
   */
  const handleFieldChange = <K extends keyof Fountain>(
    field: K,
    value: Fountain[K]
  ): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Guardar cambios
   */
  const handleSave = (): void => {
    setIsSaving(true);
    const updatedFountain = {
      ...formData,
      updatedAt: new Date(),
    };
    onSave(updatedFountain);
    setOriginalData(updatedFountain);
    setIsSaving(false);
  };

  /**
   * Cancelar edición
   */
  const handleCancel = async (): Promise<void> => {
    if (hasUnsavedChanges) {
      const shouldDiscard = await confirmDiscard();
      if (!shouldDiscard) return;
    }
    onCancel();
  };

  /**
   * Copiar código
   */
  const handleCopyCode = (): void => {
    if (formData.code) {
      navigator.clipboard.writeText(formData.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  /**
   * Eliminar fuente
   */
  const handleDelete = (): void => {
    onDelete(fountain.id);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="fountain-details-panel">
      {/* Header */}
      <div className="fountain-header">
        <div className="fountain-header-icon">
          <Droplets size={24} />
        </div>
        <div className="fountain-header-content">
          <h2 className="fountain-title">
            {formData.name || 'Fuente sin nombre'}
          </h2>
          <div className="fountain-code-section">
            {formData.code && (
              <>
                <span className="fountain-code">{formData.code}</span>
                <button
                  className="code-copy-btn"
                  onClick={handleCopyCode}
                  title="Copiar código"
                  aria-label="Copiar código"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="fountain-tabs">
        <button
          className={`fountain-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button
          className={`fountain-tab ${activeTab === 'operational' ? 'active' : ''}`}
          onClick={() => setActiveTab('operational')}
        >
          Operacional
        </button>
        <button
          className={`fountain-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          Mantenimiento
        </button>
        <button
          className={`fountain-tab ${activeTab === 'accessibility' ? 'active' : ''}`}
          onClick={() => setActiveTab('accessibility')}
        >
          Accesibilidad
        </button>
      </div>

      {/* Form Content */}
      <div className="fountain-form">
        {/* GENERAL TAB */}
        {activeTab === 'general' && (
          <div className="fountain-tab-content">
            <div className="form-group">
              <label>Nombre</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="Ej: Fuente de la Plaza Central"
                disabled={readOnly}
              />
            </div>

            <div className="form-group">
              <label>Tipo *</label>
              <select
                value={formData.type}
                onChange={(e) => handleFieldChange('type', e.target.value as FountainType)}
                disabled={readOnly}
                required
              >
                {FOUNTAIN_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                value={formData.address || ''}
                onChange={(e) => handleFieldChange('address', e.target.value)}
                placeholder="Calle y número"
                disabled={readOnly}
              />
            </div>

            <div className="form-group">
              <label>Barrio/Zona</label>
              <input
                type="text"
                value={formData.neighborhood || ''}
                onChange={(e) => handleFieldChange('neighborhood', e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div className="form-group">
              <label>Material</label>
              <input
                type="text"
                value={formData.material || ''}
                onChange={(e) => handleFieldChange('material', e.target.value)}
                placeholder="Ej: Hormigón, piedra, metal"
                disabled={readOnly}
              />
            </div>

            <div className="form-group">
              <label>Coordenadas</label>
              <input
                type="text"
                value={`${formData.coordinates[1].toFixed(6)}, ${formData.coordinates[0].toFixed(6)}`}
                disabled
                className="coordinates-input"
              />
            </div>
          </div>
        )}

        {/* OPERATIONAL TAB */}
        {activeTab === 'operational' && (
          <div className="fountain-tab-content">
            <div className="form-group">
              <label>Estado *</label>
              <select
                value={formData.status}
                onChange={(e) => handleFieldChange('status', e.target.value as FountainStatus)}
                disabled={readOnly}
                required
              >
                {FOUNTAIN_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {formData.type !== 'decorative' && (
              <>
                <div className="form-group">
                  <label>Calidad del agua</label>
                  <select
                    value={formData.waterQuality || ''}
                    onChange={(e) => handleFieldChange('waterQuality', e.target.value as WaterQuality)}
                    disabled={readOnly}
                  >
                    {WATER_QUALITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Caudal (L/min)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={formData.flowRate || ''}
                    onChange={(e) => handleFieldChange('flowRate', parseFloat(e.target.value) || undefined)}
                    disabled={readOnly}
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.hasFilter || false}
                      onChange={(e) => handleFieldChange('hasFilter', e.target.checked)}
                      disabled={readOnly}
                    />
                    Tiene filtro de agua
                  </label>
                </div>
              </>
            )}

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.hasDrainage || false}
                  onChange={(e) => handleFieldChange('hasDrainage', e.target.checked)}
                  disabled={readOnly}
                />
                Tiene sistema de drenaje
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.hasLighting || false}
                  onChange={(e) => handleFieldChange('hasLighting', e.target.checked)}
                  disabled={readOnly}
                />
                Iluminación nocturna
              </label>
            </div>
          </div>
        )}

        {/* MAINTENANCE TAB */}
        {activeTab === 'maintenance' && (
          <div className="fountain-tab-content">
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.needsIntervention || false}
                  onChange={(e) => handleFieldChange('needsIntervention', e.target.checked)}
                  disabled={readOnly}
                />
                Necesita intervención
              </label>
            </div>

            {formData.needsIntervention && (
              <div className="form-group">
                <label>Prioridad</label>
                <select
                  value={formData.priority || ''}
                  onChange={(e) => handleFieldChange('priority', e.target.value as any)}
                  disabled={readOnly}
                >
                  <option value="">No especificada</option>
                  <option value="low">Baja</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Responsable asignado</label>
              <input
                type="text"
                value={formData.assignedResponsible || ''}
                onChange={(e) => handleFieldChange('assignedResponsible', e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div className="form-group">
              <label>Notas de mantenimiento</label>
              <textarea
                value={formData.maintenanceNotes || ''}
                onChange={(e) => handleFieldChange('maintenanceNotes', e.target.value)}
                rows={4}
                disabled={readOnly}
              />
            </div>
          </div>
        )}

        {/* ACCESSIBILITY TAB */}
        {activeTab === 'accessibility' && (
          <div className="fountain-tab-content">
            <div className="form-group">
              <label>Nivel de accesibilidad</label>
              <select
                value={formData.accessibilityLevel || ''}
                onChange={(e) => handleFieldChange('accessibilityLevel', e.target.value as AccessibilityLevel)}
                disabled={readOnly}
              >
                {ACCESSIBILITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.hasWheelchairAccess || false}
                  onChange={(e) => handleFieldChange('hasWheelchairAccess', e.target.checked)}
                  disabled={readOnly}
                />
                Acceso para sillas de ruedas
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.hasPetBowl || false}
                  onChange={(e) => handleFieldChange('hasPetBowl', e.target.checked)}
                  disabled={readOnly}
                />
                Bebedero para mascotas
              </label>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.childFriendly || false}
                  onChange={(e) => handleFieldChange('childFriendly', e.target.checked)}
                  disabled={readOnly}
                />
                Apta para niños
              </label>
            </div>

            <div className="form-group">
              <label>Propietario</label>
              <select
                value={formData.owner || ''}
                onChange={(e) => handleFieldChange('owner', e.target.value as any)}
                disabled={readOnly}
              >
                <option value="">No especificado</option>
                <option value="municipal">Municipal</option>
                <option value="provincial">Provincial</option>
                <option value="private">Privado</option>
                <option value="other">Otro</option>
              </select>
            </div>

            <div className="form-group">
              <label>Observaciones generales</label>
              <textarea
                value={formData.generalNotes || ''}
                onChange={(e) => handleFieldChange('generalNotes', e.target.value)}
                rows={5}
                disabled={readOnly}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="fountain-footer">
        {!readOnly && (
          <>
            <button
              className="btn-secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
            >
              {isSaving ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              className="btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSaving}
              title="Eliminar fuente"
            >
              <Trash2 size={16} />
            </button>
          </>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <ConfirmDialog
          title="Eliminar fuente"
          message={`¿Estás seguro que deseas eliminar la fuente "${formData.name || formData.code}"?`}
          confirmLabel="Eliminar"
          cancelLabel="Cancelar"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}
